import { createStore } from "zustand/vanilla";

import {
  EXPORT_JOB_BUDGET_MS,
  EXPORT_JOB_MAX_POLL_MS,
  MAX_TRACKED_EXPORT_JOBS,
} from "./config";
import { judgeDeadline, judgeObservation, judgeSlow } from "./machine";
import type {
  ExportJobAdapter,
  ExportJobObservation,
  ExportJobVerdict,
  ExportReadyHandler,
  PollingExportJob,
  ReadyExportJob,
  TrackedExportJob,
} from "./types";

interface ExportJobsState {
  /** Live jobs only (`starting` | `polling`); a settled job leaves at once. */
  jobs: readonly TrackedExportJob[];
}

/**
 * Written only from event handlers and effects. Every transition reads and
 * writes synchronously, so it is a compare-and-set: of two callers racing to
 * settle a job, exactly one gets the settlement back.
 */
export const exportJobsStore = createStore<ExportJobsState>(() => ({
  jobs: [],
}));

export type ExportJobRefusal = "busy" | "limit";

export interface ExportJobSettlement {
  /** Polling for every verdict but a start's `timed_out`. */
  job: TrackedExportJob;
  verdict: ExportJobVerdict;
}

export interface BeginExportJobInput {
  scope: string;
  label: string;
  adapter: ExportJobAdapter;
  format?: string | null;
  filename?: string | null;
  /** A selection or filter the user can shrink (not a retry, not one profile). */
  reducible?: boolean;
}

let sequence = 0;

export function isPollingExportJob(
  job: TrackedExportJob,
): job is PollingExportJob {
  return job.phase === "polling";
}

/**
 * Whether a job holds its scope and a slot. A slow one keeps polling and still
 * downloads, but lets go at the budget instead of blocking up to the poll cap.
 */
export function isBlockingExportJob(job: TrackedExportJob): boolean {
  return !job.slow;
}

function findJob(key: string): TrackedExportJob | undefined {
  return exportJobsStore.getState().jobs.find((job) => job.key === key);
}

function replaceJob(key: string, next: TrackedExportJob | null) {
  exportJobsStore.setState(({ jobs }) => ({
    jobs: next
      ? jobs.map((job) => (job.key === key ? next : job))
      : jobs.filter((job) => job.key !== key),
  }));
}

/**
 * `starting`, synchronously and before any request, so dedupe covers the
 * POST. The budget runs from here: a start that never answers times out too.
 */
export function beginExportJob(
  input: BeginExportJobInput,
  now: number,
): { ok: true; job: TrackedExportJob } | { ok: false; reason: ExportJobRefusal } {
  const { jobs } = exportJobsStore.getState();
  const blocking = jobs.filter(isBlockingExportJob);
  if (blocking.some((job) => job.scope === input.scope)) {
    return { ok: false, reason: "busy" };
  }
  if (blocking.length >= MAX_TRACKED_EXPORT_JOBS) {
    return { ok: false, reason: "limit" };
  }

  sequence += 1;
  const job: TrackedExportJob = {
    key: `${input.scope}#${sequence}`,
    scope: input.scope,
    label: input.label,
    format: input.format ?? null,
    filename: input.filename ?? null,
    adapter: input.adapter,
    reducible: input.reducible ?? false,
    phase: "starting",
    jobId: null,
    startedAt: now,
    pollingSince: null,
    deadlineAt: now + EXPORT_JOB_BUDGET_MS,
    slow: false,
    errorStreak: 0,
    clientErrorStreak: 0,
    lastOkAt: null,
    lastObservedAt: 0,
    noticeId: null,
  };
  exportJobsStore.setState({ jobs: [...jobs, job] });
  return { ok: true, job };
}

/** The job's loading toast; null once the user closed it. */
export function setExportJobNotice(key: string, noticeId: string | number | null) {
  const job = findJob(key);
  if (job) replaceJob(key, { ...job, noticeId });
}

/** `starting` → `polling`, up to the poll cap. False once the job was cancelled or timed out. */
export function attachExportJob(key: string, jobId: string, now: number) {
  const job = findJob(key);
  if (job?.phase !== "starting") return false;
  replaceJob(key, {
    ...job,
    phase: "polling",
    jobId,
    pollingSince: now,
    deadlineAt: now + EXPORT_JOB_MAX_POLL_MS,
  });
  return true;
}

/** Removes a job whose start request failed; null once it was cancelled or timed out. */
export function dropExportJob(key: string): TrackedExportJob | null {
  const job = findJob(key);
  if (job?.phase !== "starting") return null;
  replaceJob(key, null);
  return job;
}

export function observeExportJob(
  key: string,
  jobId: string,
  observation: ExportJobObservation,
  visible: boolean,
): ExportJobSettlement | null {
  const job = findJob(key);
  if (!job || !isPollingExportJob(job) || job.jobId !== jobId) return null;

  const judgement = judgeObservation(job, observation, visible);
  if (!judgement) return null;

  const next = { ...job, ...judgement.progress };
  replaceJob(key, judgement.verdict ? null : next);
  return judgement.verdict ? { job: next, verdict: judgement.verdict } : null;
}

/** Marks a polling job slow; returns it only to the one caller that did. */
export function slowExportJob(
  key: string,
  now: number,
  visible: boolean,
): PollingExportJob | null {
  const job = findJob(key);
  if (!job || !isPollingExportJob(job) || !judgeSlow(job, now, visible)) return null;
  const next = { ...job, slow: true };
  replaceJob(key, next);
  return next;
}

export function expireExportJob(
  key: string,
  now: number,
  visible: boolean,
): ExportJobSettlement | null {
  const job = findJob(key);
  if (!job) return null;

  const verdict = judgeDeadline(job, now, visible);
  if (!verdict) return null;
  replaceJob(key, null);
  return { job, verdict };
}

/** `cancelled`: org switch, logout, host unmount. Returns what was dropped. */
export function cancelExportJobs(): readonly TrackedExportJob[] {
  const { jobs } = exportJobsStore.getState();
  if (jobs.length > 0) exportJobsStore.setState({ jobs: [] });
  return jobs;
}

const readyHandlers = new Map<string, ExportReadyHandler[]>();

/** Returns the unregister. */
export function registerExportReadyHandler(
  scope: string,
  handler: ExportReadyHandler,
): () => void {
  readyHandlers.set(scope, [...(readyHandlers.get(scope) ?? []), handler]);
  return () => {
    const rest = (readyHandlers.get(scope) ?? []).filter((h) => h !== handler);
    if (rest.length > 0) readyHandlers.set(scope, rest);
    else readyHandlers.delete(scope);
  };
}

export function runExportReadyHandlers(job: ReadyExportJob): void {
  for (const handler of readyHandlers.get(job.scope) ?? []) handler(job);
}
